import { useRef, useState } from 'react';

/** Said beside "Send a new code" once Paige took the request. */
export const codeOnItsWay = 'A new code is on its way.';

/** A "send it again" button's state: off while it runs, then one line saying it went or why not. */
export interface Resend {
  run: () => void;
  busy: boolean;
  /** What to say in the step's status line; null before the first press and while one runs. */
  said: string | null;
  /** Why it was not sent, in a sentence; null otherwise. */
  problem: string | null;
  /** Forgets what was said: the step was left. */
  clear: () => void;
}

const quiet = { busy: false, said: null, problem: null } as const;

/**
 * Sends again, one request at a time: five quick presses were five mails and the page said nothing,
 * so nobody could tell a code had gone (pass 3, 2026-10-06). `why` words a failure (the wait, when
 * the API refuses with one).
 */
export function useResend(
  send: () => Promise<unknown>,
  why: (e: unknown) => string,
  sent: string = codeOnItsWay,
): Resend {
  const [state, setState] = useState<Pick<Resend, 'busy' | 'said' | 'problem'>>(quiet);
  const running = useRef(false);
  const run = (): void => {
    if (running.current) return;
    running.current = true;
    setState({ busy: true, said: null, problem: null });
    send()
      .then(
        () => setState({ busy: false, said: sent, problem: null }),
        (e: unknown) => setState({ busy: false, said: null, problem: why(e) }),
      )
      .finally(() => {
        running.current = false;
      });
  };
  return { ...state, run, clear: () => setState(quiet) };
}
